import {
  CardDataInter,
  CustomProps,
  GameCardCategory,
  generateCustomProps
} from '../../../common.type'
import { GameFavorites } from '@/views/game/classes'
import { PlatformCategories } from '@/api/game/type'
import {
  PlatformCategoriesItem,
  PlatformCategoriesItemInter
} from '@/service/game/entity/gameCategorie'
import { PlatformGameCatgory } from '@/views/game/consts'
import { VerticalProps } from '.'
import { gameOpener } from '@/views/game/services'
import { useGameStore } from '@/views/game/store'
import AnchorHot, { AnchorHotInter } from '@/service/game/entity/anchorHot'
import GameItem, { GameItemInter } from '@/service/game/entity/gameItem'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any
/** 转换Props类型声明 */
export interface GetProps<T extends CardDataInter = CardDataInter> {
  <Data extends T = T>(
    data: Data,
    custom?: CustomProps<Data, VerticalProps>
  ): VerticalProps
}

/** 获得props */
export const getProps: GetProps = (data, custom) => {
  if (PlatformCategoriesItem.isThisType(data)) {
    return getPropsByPlatformCategoriesItem(data, custom)
  } else if (GameItem.isThisType(data)) {
    return getPropsByGameItem(data, custom)
  } else if (AnchorHot.isThisType(data)) {
    return getPropsByAnchorHot(data, custom)
  } else {
    return getPropsByPlatformCategories(
      data as Any,
      custom as CustomProps<PlatformCategories, VerticalProps>
    )
  }
}

/** 根据游戏项 */
const getPropsByGameItem: GetProps<GameItemInter> = (data, custom?) => {
  /** 生成与收藏相关 */
  const generateFavoritedRelation = () => {
    const { gameId, maintainStatus } = data
    const { currentAllFavoriteList } = useGameStore()
    const isShowFavorited = Boolean(
      gameId && !!currentAllFavoriteList && !maintainStatus
    )
    const isFavorited = currentAllFavoriteList?.includes(gameId)
    return { isShowFavorited, isFavorited }
  }
  /** 生成的props */
  const props: VerticalProps = {
    ...generateFavoritedRelation(),
    isCompleteImage: true,
    inCardRenderCategory: GameCardCategory.Game,
    isCustomIcon: data.isCustomIcon,
    imgUrl: data.gameIcon,
    logoUrl: data.platformLogo,
    categoryId: data.gameCategoryId,
    platformId: data.platformId,
    status: data.recommendStatus ? 'remenber' : undefined,
    isMaintain: data.maintainStatus,
    gameName: data.gameName,
    secondaryName: data.platformName,
    handleClickCard: () => gameOpener.openGame(data),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  }
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}

/** 根据主播游戏项 */
const getPropsByAnchorHot: GetProps<AnchorHotInter> = (data, custom?) => {
  const props: VerticalProps = {
    inCardRenderCategory: GameCardCategory.Game,
    imgUrl: data.gameIcon,
    // 主播游戏数据有gameName,但是要求不展示名称. bug单: https://www.cg.xxx/bug-view-135160.html
    gameName: '',
    handleClickCard: () => gameOpener.openGame(data),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  }
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}

/** 根据平台类型生成配置项 */
const getPropsByPlatformCategoriesItem: GetProps<
  PlatformCategoriesItemInter
> = (data, custom?) => {
  /** 生成与收藏相关 */
  const generateFavoritedRelation = () => {
    const { gameId, maintainStatus } = data
    const { currentAllFavoriteList } = useGameStore()
    const isShowFavorited = Boolean(
      gameId && !!currentAllFavoriteList && !maintainStatus
    )
    const isFavorited = currentAllFavoriteList?.includes(gameId)
    return { isShowFavorited, isFavorited }
  }
  /** 生成的props */
  const props: VerticalProps = {
    ...generateFavoritedRelation(),
    inCardRenderCategory: GameCardCategory.Platform,
    logoUrl: data.platformLogo,
    imgUrl: data.platformIcon,
    categoryId: data.gameCategoryId,
    platformId: data.platformId,
    isCompleteImage: true,
    isCustomIcon: data.isCustomIcon,
    isMaintain: data.maintainStatus,
    gameName: data.brand,
    secondaryName: data.platformName,
    handleClickCard: () => gameOpener.openGame(data),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  }
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}

/** 旧版的数据结构 */
const getPropsByPlatformCategories: GetProps<PlatformCategories> = (
  data,
  custom?
) => {
  /** 生成图片相关 */
  const generateImgRelation = () => {
    const { gameIconLarge, largeIcon, gameIcon } = data
    const ret = {} as Pick<VerticalProps, 'imgUrl' | 'isCompleteImage'>
    // 如果是用户自定义整图,则直接返回用户自定义图
    if (Boolean(data.customLargeIcon)) {
      ret.imgUrl = gameIcon || gameIconLarge || largeIcon
    }
    // 暂时注释掉垂直卡片后台合图功能,后台可能还未完成该功能
    // 如果是后台形成的合同,则直接返回后台返回的合图,优先级低于用户自定义图
    // else if (Boolean(data.isCompositePictureHot)) {
    //   ret.imgUrl = data.compositePictureHotIcon
    //   ret.isCompleteImage = true
    // }
    // 不是整张合图;
    else {
      ret.imgUrl = gameIconLarge || gameIcon || largeIcon
    }
    return ret
  }
  /** 生成 logo 相关 */
  const generateLogoUrl = () => {
    const { gameId, gameCategoryId, logo, platformIcon } = data
    const logoUrl =
      !gameId || PlatformGameCatgory.includes(gameCategoryId)
        ? logo
        : platformIcon
    return { logoUrl }
  }
  /** 生成与收藏相关 */
  const generateFavoritedRelation = () => {
    const { gameId, maintainStatus } = data
    const { currentAllFavoriteList } = useGameStore()
    const isShowFavorited = Boolean(
      gameId && !!currentAllFavoriteList && !maintainStatus
    )
    const isFavorited = currentAllFavoriteList?.includes(gameId)
    return { isShowFavorited, isFavorited }
  }
  /** 生成的props */
  const props = {
    ...generateImgRelation(),
    ...generateLogoUrl(),
    ...generateFavoritedRelation(),
    isCompleteImage: true,
    isCustomIcon: Boolean(data.customLargeIcon),
    categoryId: data.gameCategoryId,
    platformId: data.platformId,
    status: data.recommendStatus ? 'remenber' : undefined,
    isMaintain: data.maintainStatus,
    gameName: data.gameName || data.brand,
    secondaryName: data.platformName,
    handleClickCard: () =>
      gameOpener.openGame(data as Required<PlatformCategories>),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  } as unknown as VerticalProps
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}
